/**
 * SpryBoard Share Dialog (SpryWorkspace Backend)
 *
 * Replaces Nextcloud save logic with SpryWorkspace backend API.
 * Supports:
 * - Live collaboration (start/stop sessions)
 * - Share by email (via boardAPI.shareBoard)
 * - Save to SpryDrive (via spryboardAPI.save)
 * - Export shareable link
 */
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { copyTextToSystemClipboard } from "@excalidraw/excalidraw/clipboard";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import {
  copyIcon,
  LinkIcon,
  playerPlayIcon,
  playerStopFilledIcon,
  share,
  shareIOS,
  shareWindows,
} from "@excalidraw/excalidraw/components/icons";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { useCopyStatus } from "@excalidraw/excalidraw/hooks/useCopiedIndicator";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import { KEYS, getFrame } from "@excalidraw/common";
import { useCallback, useEffect, useRef, useState } from "react";

import { atom, useAtom, useAtomValue } from "../app-jotai";
import { activeRoomLinkAtom } from "../collab/Collab";
import {
  extractBoardFromResponse,
  extractSpryboardSaveFromResponse,
  spryboardAPI,
  boardAPI,
  sprymeetingAPI,
  getAccessToken,
} from "../api/backend";
import { getActiveBoardContext, setActiveBoardContext } from "../data";
import {
  parseMeetingLaunchError,
  registerLaunchTokenResponder,
  setMeetingLaunchTokenWindowName,
  type MeetingLaunchErrorState,
} from "../utils/meetingLaunch";

import "./ShareDialog.scss";

import type { CollabAPI } from "../collab/Collab";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

type OnExportToBackend = () => void;
type ShareDialogType = "share" | "collaborationOnly";

export const shareDialogStateAtom = atom<
  { isOpen: false } | { isOpen: true; type: ShareDialogType }
>({ isOpen: false });

const getShareIcon = () => {
  const navigator = window.navigator as any;
  const isAppleBrowser = /Apple/.test(navigator.vendor);
  const isWindowsBrowser = navigator.appVersion.indexOf("Win") !== -1;

  if (isAppleBrowser) {
    return shareIOS;
  } else if (isWindowsBrowser) {
    return shareWindows;
  }

  return share;
};

export type ShareDialogProps = {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  handleClose: () => void;
  onExportToBackend: OnExportToBackend;
  type: ShareDialogType;
};

type BoardPermissionRole = "viewer" | "commenter" | "editor" | "owner";

type BoardPermissionEntry = {
  userId: string;
  userName?: string;
  userEmail?: string;
  role: BoardPermissionRole;
};

const roleLabel: Record<BoardPermissionRole, string> = {
  viewer: "Viewer",
  commenter: "Commenter",
  editor: "Editor",
  owner: "Owner",
};

const getErrorMessage = (error: any) =>
  error?.response?.data?.error || error?.message || "Something went wrong";

const ActiveRoomDialog = ({
  collabAPI,
  activeRoomLink,
  handleClose,
}: {
  collabAPI: CollabAPI;
  activeRoomLink: string;
  handleClose: () => void;
}) => {
  const { t } = useI18n();
  const [, setJustCopied] = useState(false);
  const timerRef = useRef<number>(0);
  const ref = useRef<HTMLInputElement>(null);
  const isShareSupported = "share" in navigator;
  const { onCopy, copyStatus } = useCopyStatus();

  const copyRoomLink = async () => {
    try {
      await copyTextToSystemClipboard(activeRoomLink);
    } catch (e) {
      collabAPI.setCollabError(t("errors.copyToSystemClipboardFailed"));
    }

    setJustCopied(true);

    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      setJustCopied(false);
    }, 3000);

    ref.current?.select();
  };

  const shareRoomLink = async () => {
    try {
      await navigator.share({
        title: t("roomDialog.shareTitle"),
        text: t("roomDialog.shareTitle"),
        url: activeRoomLink,
      });
    } catch (error: any) {
      // ignored
    }
  };

  return (
    <>
      <h3 className="ShareDialog__active__header">
        {t("labels.liveCollaboration").replace(/\./g, "")}
      </h3>
      <TextField
        defaultValue={collabAPI.getUsername()}
        placeholder="Your name"
        label="Your name"
        onChange={collabAPI.setUsername}
        onKeyDown={(event) => event.key === KEYS.ENTER && handleClose()}
      />
      <div className="ShareDialog__active__linkRow">
        <TextField
          ref={ref}
          label="Link"
          readonly
          fullWidth
          value={activeRoomLink}
        />
        {isShareSupported && (
          <FilledButton
            size="large"
            variant="icon"
            label="Share"
            icon={getShareIcon()}
            className="ShareDialog__active__share"
            onClick={shareRoomLink}
          />
        )}
        <FilledButton
          size="large"
          label={t("buttons.copyLink")}
          icon={copyIcon}
          status={copyStatus}
          onClick={() => {
            copyRoomLink();
            onCopy();
          }}
        />
      </div>

      <BoardAccessManager />

      <div className="ShareDialog__active__description">
        <p>
          <span role="img" aria-hidden="true" className="ShareDialog__active__description__emoji">
            🔒{" "}
          </span>
          {t("roomDialog.desc_privacy")}
        </p>
        <p>{t("roomDialog.desc_exitSession")}</p>
      </div>

      <div className="ShareDialog__active__actions">
        <FilledButton
          size="large"
          variant="outlined"
          color="danger"
          label={t("roomDialog.button_stopSession")}
          icon={playerStopFilledIcon}
          onClick={() => {
            trackEvent("share", "room closed");
            collabAPI.stopCollaboration();
            if (!collabAPI.isCollaborating()) {
              handleClose();
            }
          }}
        />
      </div>
    </>
  );
};

const BoardAccessManager = () => {
  const { boardId } = getActiveBoardContext();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<BoardPermissionRole>("editor");
  const [permissions, setPermissions] = useState<BoardPermissionEntry[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [changingUserId, setChangingUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [isUpdatingPublic, setIsUpdatingPublic] = useState(false);

  const loadAccess = useCallback(async () => {
    if (!boardId) {
      setPermissions([]);
      setIsPublic(false);
      return;
    }

    setIsLoadingAccess(true);
    try {
      const [boardResponse, permissionsResponse] = await Promise.all([
        boardAPI.getBoard(boardId),
        boardAPI.getPermissions(boardId),
      ]);
      const boardPayload = extractBoardFromResponse(boardResponse);
      setIsPublic(Boolean(boardPayload?.isPublic));

      const rawPermissions = Array.isArray(permissionsResponse?.data?.permissions)
        ? (permissionsResponse.data.permissions as BoardPermissionEntry[])
        : [];

      const sortedPermissions = [...rawPermissions].sort((left, right) => {
        if (left.role === "owner" && right.role !== "owner") {
          return -1;
        }
        if (right.role === "owner" && left.role !== "owner") {
          return 1;
        }
        return String(left.userEmail || left.userName || left.userId).localeCompare(
          String(right.userEmail || right.userName || right.userId),
        );
      });

      setPermissions(sortedPermissions);
    } catch (error) {
      setStatusMessage(`Error: ${getErrorMessage(error)}`);
    } finally {
      setIsLoadingAccess(false);
    }
  }, [boardId]);

  useEffect(() => {
    void loadAccess();
  }, [loadAccess]);

  const handleShare = async () => {
    if (!boardId || !email.trim()) {
      setStatusMessage("Enter an email address to share this board.");
      return;
    }

    setIsSharing(true);
    setStatusMessage(null);
    try {
      await boardAPI.shareBoard(boardId, { email: email.trim(), role });
      setStatusMessage(`✓ Shared with ${email.trim()}`);
      setEmail("");
      trackEvent("share", "email share", "success");
      await loadAccess();
    } catch (error) {
      setStatusMessage(`Error: ${getErrorMessage(error)}`);
    } finally {
      setIsSharing(false);
    }
  };

  const handlePermissionRoleChange = async (userId: string, nextRole: BoardPermissionRole) => {
    if (!boardId) {
      return;
    }

    setChangingUserId(userId);
    setStatusMessage(null);
    try {
      await boardAPI.updatePermission(boardId, userId, nextRole);
      setStatusMessage("✓ Access updated");
      await loadAccess();
    } catch (error) {
      setStatusMessage(`Error: ${getErrorMessage(error)}`);
    } finally {
      setChangingUserId(null);
    }
  };

  const handleRemovePermission = async (userId: string, label: string) => {
    if (!boardId) {
      return;
    }
    const confirmed = window.confirm(`Remove access for ${label}?`);
    if (!confirmed) {
      return;
    }

    setRemovingUserId(userId);
    setStatusMessage(null);
    try {
      await boardAPI.removePermission(boardId, userId);
      setStatusMessage("✓ Access removed");
      await loadAccess();
    } catch (error) {
      setStatusMessage(`Error: ${getErrorMessage(error)}`);
    } finally {
      setRemovingUserId(null);
    }
  };

  const handlePublicAccessChange = async (nextValue: boolean) => {
    if (!boardId) {
      return;
    }

    setIsUpdatingPublic(true);
    setStatusMessage(null);
    try {
      await boardAPI.updatePublicAccess(boardId, nextValue);
      setIsPublic(nextValue);
      setStatusMessage(nextValue ? "✓ Public board link enabled" : "✓ Public board link disabled");
    } catch (error) {
      setStatusMessage(`Error: ${getErrorMessage(error)}`);
    } finally {
      setIsUpdatingPublic(false);
    }
  };

  if (!boardId) {
    return (
      <div className="ShareDialog__boardAccess">
        <div className="ShareDialog__boardAccess__sectionTitle">Manage access</div>
        <div className="ShareDialog__boardAccess__hint">
          Save this board to SpryDrive first so you can manage sharing and public access.
        </div>
      </div>
    );
  }

  return (
    <div className="ShareDialog__boardAccess">
      <div className="ShareDialog__boardAccess__sectionTitle">Manage access</div>

      <div className="ShareDialog__boardAccess__inviteRow">
        <TextField
          value={email}
          placeholder="user@example.com"
          label="Invite by email"
          onChange={setEmail}
          onKeyDown={(event) => event.key === KEYS.ENTER && void handleShare()}
        />
        <label className="ShareDialog__boardAccess__field">
          <span>Role</span>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value as BoardPermissionRole)}
            className="ShareDialog__boardAccess__select"
          >
            <option value="viewer">Viewer</option>
            <option value="commenter">Commenter</option>
            <option value="editor">Editor</option>
          </select>
        </label>
        <FilledButton
          size="large"
          label={isSharing ? "Sharing..." : "Share"}
          onClick={handleShare}
        />
      </div>

      <label className="ShareDialog__boardAccess__publicToggle">
        <input
          type="checkbox"
          checked={isPublic}
          onChange={(event) => void handlePublicAccessChange(event.target.checked)}
          disabled={isUpdatingPublic}
        />
        <span>
          <strong>Public board link</strong>
          <span>Anyone with the board link can open it as a viewer.</span>
        </span>
      </label>

      {statusMessage && (
        <div
          className="ShareDialog__boardAccess__status"
          data-tone={statusMessage.startsWith("✓") ? "success" : "error"}
        >
          {statusMessage}
        </div>
      )}

      <div className="ShareDialog__boardAccess__sectionTitle">People with access</div>

      {isLoadingAccess ? (
        <div className="ShareDialog__boardAccess__hint">Loading access…</div>
      ) : permissions.length === 0 ? (
        <div className="ShareDialog__boardAccess__hint">Only you can access this board right now.</div>
      ) : (
        <div className="ShareDialog__boardAccess__list">
          {permissions.map((permission) => {
            const label =
              permission.userName ||
              permission.userEmail ||
              permission.userId;
            const isOwner = permission.role === "owner";

            return (
              <div key={permission.userId} className="ShareDialog__boardAccess__item">
                <div className="ShareDialog__boardAccess__itemMeta">
                  <div className="ShareDialog__boardAccess__itemName">{label}</div>
                  <div className="ShareDialog__boardAccess__itemEmail">
                    {permission.userEmail || permission.userId}
                  </div>
                </div>
                <div className="ShareDialog__boardAccess__itemActions">
                  {isOwner ? (
                    <div className="ShareDialog__boardAccess__ownerBadge">Owner</div>
                  ) : (
                    <>
                      <select
                        value={permission.role}
                        onChange={(event) =>
                          void handlePermissionRoleChange(
                            permission.userId,
                            event.target.value as BoardPermissionRole,
                          )
                        }
                        className="ShareDialog__boardAccess__select"
                        disabled={changingUserId === permission.userId}
                      >
                        <option value="viewer">{roleLabel.viewer}</option>
                        <option value="commenter">{roleLabel.commenter}</option>
                        <option value="editor">{roleLabel.editor}</option>
                      </select>
                      <button
                        type="button"
                        className="ShareDialog__boardAccess__remove"
                        onClick={() => void handleRemovePermission(permission.userId, label)}
                        disabled={removingUserId === permission.userId}
                      >
                        {removingUserId === permission.userId ? "Removing..." : "Remove"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ShareDialogPicker = (props: ShareDialogProps) => {
  const { t } = useI18n();
  const [isSavingToSpryDrive, setIsSavingToSpryDrive] = useState(false);
  const [spryDriveSaveStatus, setSpryDriveSaveStatus] = useState<string | null>(null);
  const [isStartingMeeting, setIsStartingMeeting] = useState(false);
  const [spryMeetingStatus, setSpryMeetingStatus] = useState<MeetingLaunchErrorState | null>(null);

  const { collabAPI, excalidrawAPI } = props;

  const handleSaveToSpryDrive = async () => {
    setIsSavingToSpryDrive(true);
    setSpryDriveSaveStatus(null);

    try {
      if (!excalidrawAPI) {
        setSpryDriveSaveStatus("Editor not ready");
        return;
      }

      const appState = excalidrawAPI.getAppState();
      const elements = excalidrawAPI.getSceneElements();
      const files = excalidrawAPI.getFiles();

      const boardData = {
        type: "excalidraw",
        version: 2,
        source: window.location.origin,
        elements,
        appState: {
          viewBackgroundColor: appState?.viewBackgroundColor || "#ffffff",
          gridSize: appState?.gridSize || null,
        },
        files: files || {},
      };

      const { boardId, driveFileId, title } = getActiveBoardContext();

      const fileName = title || prompt("Enter file name:", "My SpryBoard.spryboard");
      if (!fileName) {
        setIsSavingToSpryDrive(false);
        return;
      }

      const response = await spryboardAPI.save({
        filename: fileName,
        boardData,
        fileId: driveFileId || undefined,
        boardId: boardId || undefined,
      });

      const savedData = extractSpryboardSaveFromResponse(response);
      if (savedData?.fileId || savedData?.boardId) {
        setActiveBoardContext({
          boardId: savedData.boardId || boardId || null,
          driveFileId: savedData.fileId || driveFileId || null,
          title: fileName,
        });
      }

      setSpryDriveSaveStatus("✓ Saved to SpryDrive!");
      trackEvent("share", "sprydrive save", "success");
    } catch (error: any) {
      console.error("SpryDrive save error:", error);
      setSpryDriveSaveStatus(`Error: ${error.message}`);
      trackEvent("share", "sprydrive save", "error");
    } finally {
      setIsSavingToSpryDrive(false);
    }
  };

  const handleStartSpryMeeting = async () => {
    const { boardId, driveFileId } = getActiveBoardContext();
    const docId = driveFileId || boardId;

    if (!docId) {
      setSpryMeetingStatus({
        title: "Save this board before starting a meeting",
        message: "Save this board to SpryDrive first, then start the linked SpryMeeting session.",
      });
      return;
    }

    const pendingWindow = window.open("", "_blank");
    setIsStartingMeeting(true);
    setSpryMeetingStatus(null);

    try {
      const { url } = await sprymeetingAPI.launchDocumentRoom({
        docId,
        docType: "spryboard",
        source: "spryboard",
        entryPoint: "spryboard-share-dialog",
        returnUrl: window.location.href,
      });
      const launchToken = getAccessToken();

      if (launchToken) {
        setMeetingLaunchTokenWindowName(pendingWindow ?? window, launchToken);

        if (pendingWindow) {
          registerLaunchTokenResponder({
            childWindow: pendingWindow,
            childOrigin: new URL(url).origin,
            token: launchToken,
          });
        }
      }

      if (pendingWindow) {
        pendingWindow.location.replace(url);
      } else {
        window.location.assign(url);
      }

      trackEvent("share", "sprymeeting launch", "success");
    } catch (error) {
      pendingWindow?.close();
      setSpryMeetingStatus(parseMeetingLaunchError(error, "SpryBoard"));
      trackEvent("share", "sprymeeting launch", "error");
    } finally {
      setIsStartingMeeting(false);
    }
  };

  const startCollabJSX = collabAPI ? (
    <>
      <div className="ShareDialog__picker__header">
        {t("labels.liveCollaboration").replace(/\./g, "")}
      </div>

      <div className="ShareDialog__picker__description">
        <div style={{ marginBottom: "1em" }}>{t("roomDialog.desc_intro")}</div>
        {t("roomDialog.desc_privacy")}
      </div>

      <div className="ShareDialog__picker__button">
        <FilledButton
          size="large"
          label={t("roomDialog.button_startSession")}
          icon={playerPlayIcon}
          onClick={() => {
            trackEvent("share", "room creation", `ui (${getFrame()})`);
            collabAPI.startCollaboration(null);
          }}
        />
      </div>

      {props.type === "share" && (
        <div className="ShareDialog__separator">
          <span>{t("shareDialog.or")}</span>
        </div>
      )}
    </>
  ) : null;

  return (
    <>
      {startCollabJSX}

      {props.type === "share" && (
        <>
          <div className="ShareDialog__picker__header">
            {t("exportDialog.link_title")}
          </div>
          <div className="ShareDialog__picker__description">
            {t("exportDialog.link_details")}
          </div>

          <div className="ShareDialog__picker__button">
            <FilledButton
              size="large"
              label={t("exportDialog.link_button")}
              icon={LinkIcon}
              onClick={async () => {
                await props.onExportToBackend();
                props.handleClose();
              }}
            />
          </div>

          <div className="ShareDialog__separator">
            <span>{t("shareDialog.or")}</span>
          </div>

          <div className="ShareDialog__picker__header">
            Start in SpryMeeting
          </div>
          <div className="ShareDialog__picker__description">
            Open a linked SpryMeeting room for this board so collaborators can join the call with the board context ready.
            {spryMeetingStatus && (
              <div style={{
                marginTop: "0.5em",
                display: "grid",
                gap: "0.2em",
                color: "#f38ba8",
              }}>
                <strong>{spryMeetingStatus.title}</strong>
                <span>{spryMeetingStatus.message}</span>
              </div>
            )}
          </div>

          <div className="ShareDialog__picker__button">
            <FilledButton
              size="large"
              label={isStartingMeeting ? "Starting meeting..." : "Start SpryMeeting"}
              icon={playerPlayIcon}
              onClick={handleStartSpryMeeting}
            />
          </div>

          <div className="ShareDialog__separator">
            <span>{t("shareDialog.or")}</span>
          </div>

          <div className="ShareDialog__picker__header">
            Save to SpryDrive
          </div>
          <div className="ShareDialog__picker__description">
            Save your drawing to SpryDrive for permanent storage and sharing.
            {spryDriveSaveStatus && (
              <div style={{
                marginTop: "0.5em",
                fontWeight: "bold",
                color: spryDriveSaveStatus.startsWith("✓") ? "#a6e3a1" : "#f38ba8",
              }}>
                {spryDriveSaveStatus}
              </div>
            )}
          </div>

          <div className="ShareDialog__picker__button">
            <FilledButton
              size="large"
              label={isSavingToSpryDrive ? "Saving..." : "Save to SpryDrive"}
              icon={LinkIcon}
              onClick={handleSaveToSpryDrive}
            />
          </div>

          <div className="ShareDialog__separator">
            <span>{t("shareDialog.or")}</span>
          </div>

          <BoardAccessManager />
        </>
      )}
    </>
  );
};

const ShareDialogInner = (props: ShareDialogProps) => {
  const activeRoomLink = useAtomValue(activeRoomLinkAtom);

  return (
    <Dialog size="small" onCloseRequest={props.handleClose} title={false}>
      <div className="ShareDialog">
        {props.collabAPI && activeRoomLink ? (
          <ActiveRoomDialog
            collabAPI={props.collabAPI}
            activeRoomLink={activeRoomLink}
            handleClose={props.handleClose}
          />
        ) : (
          <ShareDialogPicker {...props} />
        )}
      </div>
    </Dialog>
  );
};

export const ShareDialog = (props: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onExportToBackend: OnExportToBackend;
}) => {
  const [shareDialogState, setShareDialogState] = useAtom(shareDialogStateAtom);

  const { openDialog } = useUIAppState();

  useEffect(() => {
    if (openDialog) {
      setShareDialogState({ isOpen: false });
    }
  }, [openDialog, setShareDialogState]);

  if (!shareDialogState.isOpen) {
    return null;
  }

  return (
    <ShareDialogInner
      handleClose={() => setShareDialogState({ isOpen: false })}
      collabAPI={props.collabAPI}
      excalidrawAPI={props.excalidrawAPI}
      onExportToBackend={props.onExportToBackend}
      type={shareDialogState.type}
    />
  );
};
